import React, { useState } from 'react';
import { DocumentArrowUpIcon, XMarkIcon, CheckCircleIcon, ExclamationCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

interface PodData {
  id: string;
  address: string;
  postalCode: string;
  city: string;
  street: string;
  measurementType: string;
  annualConsumption: string;
}

interface BulkPodUploaderProps {
  onUpload: (pods: PodData[]) => void;
  onClose: () => void;
}

interface ParsedPod {
  data: PodData;
  lineNumber: number;
  isValid: boolean;
  errors: string[];
}

const BulkPodUploader: React.FC<BulkPodUploaderProps> = ({ onUpload, onClose }) => {
  const [textInput, setTextInput] = useState('');
  const [parsedPods, setParsedPods] = useState<ParsedPod[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedPodIndexes, setSelectedPodIndexes] = useState<Set<number>>(new Set());

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      const buffer = ev.target?.result as ArrayBuffer;

      // Excel fájlok (.xlsx) kezelése
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        try {
          const data = new Uint8Array(buffer);
          const workbook = XLSX.read(data, { type: 'array', cellText: true, cellDates: false });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) as any[][];

          if (rows.length === 0) {
            toast.error('Az Excel fájl üres');
            return;
          }

          // Sorok ;-vel elválasztott CSV szöveggé alakítása
          const csvText = rows
            .map(row => row.map(cell => String(cell).trim()).join(';'))
            .join('\n');
          setTextInput(csvText);
          toast.success('Excel fájl betöltve');
        } catch (error) {
          console.error('Excel parse error:', error);
          toast.error('Hiba az Excel fájl olvasása közben');
        }
        return;
      }

      // CSV/TXT fájlok kezelése (Windows-1250 vagy UTF-8)
      try {
        const decoder1250 = new TextDecoder('windows-1250');
        setTextInput(decoder1250.decode(buffer));
      } catch {
        const decoderUtf8 = new TextDecoder('utf-8');
        setTextInput(decoderUtf8.decode(buffer));
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const validatePodId = (podId: string): boolean => {
    // POD ID validáció: 33 karakter hosszú
    return podId.length === 33;
  };

  const parsePodLine = (line: string, lineNumber: number): ParsedPod => {
    const errors: string[] = [];
    
    // Támogatott formátumok:
    // 1. POD_ID;Cím;Típus;Fogyasztás (4 oszlop)
    // 2. POD_ID|Cím|Típus|Fogyasztás (4 oszlop)
    // 3. POD_ID\tCím\tTípus\tFogyasztás (4 oszlop, tab)
    // 4. POD_ID (csak POD ID)
    // 5. POD_ID;Cím;Irányítószám;Város;Utca;Típus;Fogyasztás (7 oszlop)
    
    let parts: string[];
    if (line.includes(';')) {
      parts = line.split(';');
    } else if (line.includes('|')) {
      parts = line.split('|');
    } else if (line.includes('\t')) {
      parts = line.split('\t');
    } else {
      parts = [line];
    }

    const podId = parts[0]?.trim() || '';

    let address = '';
    let postalCode = '';
    let city = '';
    let street = '';
    let measurementType = 'SLP';
    let annualConsumption = '0';

    if (parts.length >= 7) {
      // 7 oszlopos formátum: POD;Cím;Irányítószám;Város;Utca;Típus;Fogyasztás
      address = parts[1]?.trim() || '';
      postalCode = parts[2]?.trim() || '';
      city = parts[3]?.trim() || '';
      street = parts[4]?.trim() || '';
      measurementType = parts[5]?.trim() || 'SLP';
      annualConsumption = (parts[6]?.trim() || '0').replace(/\s+/g, '');
    } else if (parts.length >= 4) {
      // 4 oszlopos formátum: POD;Cím;Típus;Fogyasztás
      address = parts[1]?.trim() || '';
      measurementType = parts[2]?.trim() || 'SLP';
      annualConsumption = (parts[3]?.trim() || '0').replace(/\s+/g, '');
    }

    // Validációk
    if (!podId) {
      errors.push('POD ID hiányzik');
    } else if (!validatePodId(podId)) {
      errors.push('POD ID nem 33 karakter hosszú');
    }

    if (measurementType && !['AMR', 'SLP', 'amr', 'slp'].includes(measurementType)) {
      errors.push('Típus csak AMR vagy SLP lehet');
    }

    const data: PodData = {
      id: podId,
      address,
      postalCode,
      city,
      street,
      measurementType: measurementType.toUpperCase(),
      annualConsumption
    };

    return {
      data,
      lineNumber,
      isValid: errors.length === 0,
      errors
    };
  };

  const handleParse = () => {
    if (!textInput.trim()) {
      toast.error('Adj meg legalább egy POD-ot!');
      return;
    }

    let lines = textInput.split('\n').filter(line => line.trim());

    // Az első sort mindig kihagyjuk, mert az a fejléc
    if (lines.length > 0) {
      lines = lines.slice(1);
    }

    const parsed = lines.map((line, index) => parsePodLine(line, index + 1));
    
    setParsedPods(parsed);
    setShowPreview(true);

    // Alapértelmezetten minden érvényes POD kijelölve
    const validIndexes = new Set(parsed.map((p, index) => p.isValid ? index : -1).filter(i => i !== -1));
    setSelectedPodIndexes(validIndexes);

    const validCount = parsed.filter(p => p.isValid).length;
    const invalidCount = parsed.filter(p => !p.isValid).length;

    if (invalidCount > 0) {
      toast.error(`${invalidCount} hibás sor található!`);
    } else {
      toast.success(`${validCount} POD sikeresen feldolgozva!`);
    }
  };

  const handleUpload = () => {
    const selectedPods = parsedPods
      .filter((p, index) => p.isValid && selectedPodIndexes.has(index))
      .map(p => p.data);
    
    if (selectedPods.length === 0) {
      toast.error('Nincs kijelölt érvényes POD feltöltésre!');
      return;
    }

    onUpload(selectedPods);
    toast.success(`${selectedPods.length} POD hozzáadva!`);
    onClose();
  };

  const downloadExampleExcel = () => {
    const link = document.createElement('a');
    link.href = '/PodMinta.xlsx';
    link.download = 'PodMinta.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('POD minta Excel letöltve');
  };

  const exampleText = `HU0012345678901234567890123456789;Budapest, Fő utca 1.;1051;Budapest;Fő utca 1.;AMR;150000
HU0012345678901234567890123456790;Budapest, Kossuth tér 5.;1055;Budapest;Kossuth tér 5.;SLP;80000
HU0012345678901234567890123456791;Debrecen, Petőfi tér 10.;4025;Debrecen;Petőfi tér 10.;AMR;200000`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DocumentArrowUpIcon className="h-8 w-8 text-white" />
              <div>
                <h2 className="text-2xl font-bold text-white">POD-ok tömeges feltöltése</h2>
                <p className="text-sm text-white/80 mt-1">Másold be a POD adatokat soronként</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-lg"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!showPreview ? (
            <div className="space-y-4">
              {/* Példa formátum */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-blue-900">📋 Ajánlott formátum (Excel/CSV):</h3>
                  <button
                    onClick={downloadExampleExcel}
                    className="flex items-center gap-1.5 text-xs text-primary-700 hover:text-primary-800 font-semibold bg-white border border-primary-300 rounded-lg px-3 py-1.5 hover:bg-primary-50 transition-colors"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Excel minta letöltése
                  </button>
                </div>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><code className="bg-blue-100 px-2 py-1 rounded">POD_ID;Cím;Irányítószám;Város;Utca;Típus;Éves_fogyasztás_kWh</code> <span className="text-xs text-emerald-600 font-semibold">7 oszlop</span></p>
                  <p className="text-xs mt-2">
                    <strong>Típus:</strong> AMR vagy SLP (opcionális, alapértelmezett: SLP)<br />
                    <strong>Fogyasztás:</strong> Éves fogyasztás kWh-ban (opcionális)
                  </p>
                </div>
              </div>

              {/* Automatikus cím szétbontás info */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-emerald-900 mb-1">🔄 Automatikus cím szétbontás</h3>
                <p className="text-xs text-emerald-700">
                  A címek automatikusan szét lesznek bontva feltöltéskor. <br />
                  Formátum: <code className="bg-emerald-100 px-1 rounded">1234 Város, Utca 1.</code> → Irányítószám / Város / Utca
                </p>
              </div>

              {/* Példa adatok */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">💡 Példa:</h3>
                  <button
                    onClick={() => setTextInput(exampleText)}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Példa betöltése
                  </button>
                </div>
                <pre className="text-xs text-gray-700 bg-white p-3 rounded border border-gray-200 overflow-x-auto">
                  {exampleText}
                </pre>
              </div>

              {/* Textarea */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    POD adatok (soronként egy POD)
                  </label>
                  <label className="cursor-pointer flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 font-medium border border-primary-300 rounded-lg px-3 py-1.5 hover:bg-primary-50 transition-colors">
                    <DocumentArrowUpIcon className="h-4 w-4" />
                    Excel / CSV betöltése
                    <input type="file" accept=".csv,.txt,.xlsx,.xls" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
                <textarea
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Másold be a POD adatokat ide..."
                  rows={12}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-2">
                  {textInput.split('\n').filter(l => l.trim()).length} sor
                </p>
              </div>

              {/* Parse gomb */}
              <button
                onClick={handleParse}
                disabled={!textInput.trim()}
                className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
              >
                Feldolgozás és előnézet
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Összesítő */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="h-6 w-6 text-green-600" />
                    <div>
                      <p className="text-sm text-green-600 font-medium">Érvényes POD-ok</p>
                      <p className="text-2xl font-bold text-green-700">
                        {parsedPods.filter(p => p.isValid).length}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="selectAllPods"
                      checked={selectedPodIndexes.size === parsedPods.filter(p => p.isValid).length && parsedPods.filter(p => p.isValid).length > 0}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const allValid = new Set(parsedPods.map((p, index) => p.isValid ? index : -1).filter(i => i !== -1));
                          setSelectedPodIndexes(allValid);
                        } else {
                          setSelectedPodIndexes(new Set());
                        }
                      }}
                      className="h-5 w-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                    />
                    <label htmlFor="selectAllPods" className="text-sm text-blue-800 font-medium cursor-pointer">
                      Kijelölve: {selectedPodIndexes.size}
                    </label>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <ExclamationCircleIcon className="h-6 w-6 text-red-600" />
                    <div>
                      <p className="text-sm text-red-600 font-medium">Hibás POD-ok</p>
                      <p className="text-2xl font-bold text-red-700">
                        {parsedPods.filter(p => !p.isValid).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* POD lista */}
              <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                {parsedPods.map((pod, index) => {
                  const isAMR = pod.data.measurementType === 'AMR' || pod.data.measurementType === 'negyedoras';
                  const cons = parseInt(pod.data.annualConsumption || '0', 10);
                  const consFormatted = cons ? cons.toLocaleString('hu-HU') : '—';
                  return (
                    <div
                      key={index}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all ${
                        pod.isValid
                          ? 'bg-white border-gray-200 hover:border-primary-300 hover:shadow-sm'
                          : 'bg-red-50 border-red-200'
                      }`}
                    >
                      {/* Kijelölés checkbox */}
                      {pod.isValid && (
                        <input
                          type="checkbox"
                          checked={selectedPodIndexes.has(index)}
                          onChange={(e) => {
                            const newSelected = new Set(selectedPodIndexes);
                            if (e.target.checked) {
                              newSelected.add(index);
                            } else {
                              newSelected.delete(index);
                            }
                            setSelectedPodIndexes(newSelected);
                          }}
                          className="h-5 w-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                        />
                      )}

                      {/* Sorszám */}
                      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-semibold text-gray-500">
                        {pod.lineNumber}
                      </div>

                      {/* POD ID */}
                      <div className="flex-shrink-0 w-64">
                        <p className="text-xs text-gray-400 mb-0.5">POD azonosító</p>
                        <p className="font-mono text-xs font-semibold text-gray-800 truncate">
                          {pod.data.id || '—'}
                        </p>
                      </div>

                      {/* Cím */}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-400 mb-0.5">Fogyasztási hely</p>
                        <p className="text-sm text-gray-700 truncate">
                          {pod.data.address || <span className="text-gray-400 italic">nincs megadva</span>}
                        </p>
                        {pod.data.postalCode && (
                          <p className="text-xs text-emerald-600">
                            {pod.data.postalCode} {pod.data.city} — {pod.data.street}
                          </p>
                        )}
                      </div>

                      {/* Típus badge */}
                      <div className="flex-shrink-0 text-center">
                        <p className="text-xs text-gray-400 mb-0.5">Típus</p>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${
                          isAMR
                            ? 'bg-blue-100 text-blue-700 ring-1 ring-blue-200'
                            : 'bg-amber-100 text-amber-700 ring-1 ring-amber-200'
                        }`}>
                          {isAMR ? '⚡ AMR' : '📊 SLP'}
                        </span>
                      </div>

                      {/* Fogyasztás */}
                      <div className="flex-shrink-0 text-right w-28">
                        <p className="text-xs text-gray-400 mb-0.5">Éves fogy.</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {consFormatted} <span className="text-xs font-normal text-gray-500">kWh</span>
                        </p>
                      </div>

                      {/* Státusz */}
                      <div className="flex-shrink-0 w-6">
                        {pod.isValid ? (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        ) : (
                          <div className="relative group">
                            <ExclamationCircleIcon className="h-5 w-5 text-red-500 cursor-pointer" />
                            <div className="absolute right-0 bottom-7 hidden group-hover:block z-10 bg-red-700 text-white text-xs rounded-lg px-2 py-1 w-48 shadow-lg">
                              {pod.errors.join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Akciógombok */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPreview(false);
                    setParsedPods([]);
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                >
                  Vissza a szerkesztéshez
                </button>
                <button
                  onClick={handleUpload}
                  disabled={selectedPodIndexes.size === 0}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {selectedPodIndexes.size} kijelölt POD hozzáadása
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkPodUploader;
